
import { useState } from 'react';

const Agreements = () => {
  const [activeSection, setActiveSection] = useState('agreements');

  const agreements = [
    {
      id: 1,
      title: 'Master Service Agreement',
      type: 'Contract',
      status: 'Signed',
      signedDate: '2024-01-15',
      expiryDate: '2025-01-15',
      parties: ['TechCorp Solutions', 'ProjectHub Inc.'],
      value: '$25,000',
      description: 'Primary contract outlining scope, terms, and conditions for the website redesign project.'
    },
    {
      id: 2,
      title: 'Non-Disclosure Agreement',
      type: 'NDA',
      status: 'Signed',
      signedDate: '2024-01-12',
      expiryDate: '2026-01-12',
      parties: ['TechCorp Solutions', 'ProjectHub Inc.'],
      value: 'N/A',
      description: 'Confidentiality agreement protecting sensitive business information and trade secrets.'
    },
    {
      id: 3,
      title: 'Change Order #001',
      type: 'Amendment',
      status: 'Pending',
      signedDate: null,
      expiryDate: null,
      parties: ['TechCorp Solutions', 'ProjectHub Inc.'],
      value: '+$3,500',
      description: 'Additional mobile app integration requirements requested by client.'
    }
  ];

  const milestones = [
    {
      id: 1,
      title: 'Project Kickoff',
      status: 'Completed',
      dueDate: '2024-01-15',
      completedDate: '2024-01-15',
      deliverables: ['Project charter', 'Team assignments', 'Communication plan'],
      payment: '$5,000'
    },
    {
      id: 2,
      title: 'Design Phase Completion',
      status: 'Completed',
      dueDate: '2024-01-25',
      completedDate: '2024-01-24',
      deliverables: ['Wireframes', 'Design mockups', 'Style guide'],
      payment: '$8,000'
    },
    {
      id: 3,
      title: 'Development Phase 1',
      status: 'In Progress',
      dueDate: '2024-02-05',
      completedDate: null,
      deliverables: ['Homepage development', 'Navigation system', 'Responsive layout'],
      payment: '$7,000'
    },
    {
      id: 4,
      title: 'Testing & Launch',
      status: 'Pending',
      dueDate: '2024-02-15',
      completedDate: null,
      deliverables: ['Quality assurance', 'User testing', 'Production deployment'],
      payment: '$5,000'
    }
  ];

  const closeoutItems = [
    {
      id: 1,
      item: 'Final deliverables handover',
      status: 'Pending',
      assignee: 'Sarah Johnson',
      dueDate: '2024-02-15'
    },
    {
      id: 2,
      item: 'Client training sessions',
      status: 'Scheduled',
      assignee: 'Mike Rodriguez',
      dueDate: '2024-02-16'
    },
    {
      id: 3,
      item: 'Documentation completion',
      status: 'In Progress',
      assignee: 'Lisa Kim',
      dueDate: '2024-02-14'
    },
    {
      id: 4,
      item: 'Final invoice submission',
      status: 'Pending',
      assignee: 'Finance Team',
      dueDate: '2024-02-17'
    },
    {
      id: 5,
      item: 'Project retrospective meeting',
      status: 'Scheduled',
      assignee: 'Sarah Johnson',
      dueDate: '2024-02-18'
    }
  ];

  const getStatusColor = (status: string) => {
    const colors = {
      'Signed': 'bg-green-100 text-green-800',
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'Scheduled': 'bg-purple-100 text-purple-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Section Navigation */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => setActiveSection('agreements')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activeSection === 'agreements'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <i className="ri-contract-line mr-2"></i>
          Agreements
        </button>
        <button
          onClick={() => setActiveSection('milestones')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activeSection === 'milestones'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <i className="ri-flag-line mr-2"></i>
          Milestones
        </button>
        <button
          onClick={() => setActiveSection('closeout')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activeSection === 'closeout'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <i className="ri-checkbox-line mr-2"></i>
          Closeout
        </button>
      </div>

      {/* Agreements Section */}
      {activeSection === 'agreements' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Project Agreements</h3>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap">
              <i className="ri-add-line mr-2"></i>
              New Agreement
            </button>
          </div>

          <div className="space-y-4">
            {agreements.map((agreement) => (
              <div key={agreement.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-1">{agreement.title}</h4>
                    <p className="text-sm text-gray-600">{agreement.description}</p>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(agreement.status)}`}>
                    {agreement.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Type</label>
                    <p className="text-sm text-gray-900">{agreement.type}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Value</label>
                    <p className="text-sm text-gray-900 font-semibold">{agreement.value}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Signed Date</label>
                    <p className="text-sm text-gray-900">{agreement.signedDate || 'Not signed'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</label>
                    <p className="text-sm text-gray-900">{agreement.expiryDate || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Parties</label>
                    <p className="text-sm text-gray-900">{agreement.parties.join(' • ')}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
                      <i className="ri-eye-line mr-1"></i>
                      View
                    </button>
                    <button className="px-3 py-1 text-sm text-gray-600 hover:text-gray-700 font-medium">
                      <i className="ri-download-line mr-1"></i>
                      Download
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Milestones Section */}
      {activeSection === 'milestones' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Project Milestones</h3>
            <div className="text-sm text-gray-600">
              Total Value: <span className="font-semibold text-gray-900">$25,000</span>
            </div>
          </div>

          <div className="space-y-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.id} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      milestone.status === 'Completed' ? 'bg-green-100' :
                      milestone.status === 'In Progress' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {milestone.status === 'Completed' ? (
                        <i className="ri-check-line text-green-600"></i>
                      ) : milestone.status === 'In Progress' ? (
                        <i className="ri-time-line text-blue-600"></i>
                      ) : (
                        <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-1">{milestone.title}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(milestone.status)}`}>
                          {milestone.status}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">{milestone.payment}</p>
                        <p className="text-xs text-gray-500">Payment</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</label>
                        <p className="text-sm text-gray-900">{milestone.dueDate}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Completed Date</label>
                        <p className="text-sm text-gray-900">{milestone.completedDate || 'Not completed'}</p>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">Deliverables</label>
                      <div className="flex flex-wrap gap-2">
                        {milestone.deliverables.map((deliverable, idx) => (
                          <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md">
                            {deliverable}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Closeout Section */}
      {activeSection === 'closeout' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Project Closeout Checklist</h3>
            <div className="text-sm text-gray-600">
              Progress: <span className="font-semibold text-gray-900">2/5 Complete</span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="col-span-5">Task</div>
                <div className="col-span-2">Assignee</div>
                <div className="col-span-2">Due Date</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1">Action</div>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {closeoutItems.map((item) => (
                <div key={item.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-5">
                      <p className="text-sm font-medium text-gray-900">{item.item}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-gray-900">{item.assignee}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-gray-900">{item.dueDate}</p>
                    </div>
                    <div className="col-span-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <button className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                        <i className="ri-more-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-blue-900">Closeout Progress</h4>
              <span className="text-sm text-blue-700">40% Complete</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full" style={{ width: '40%' }}></div>
            </div>
            <p className="text-xs text-blue-700 mt-1">3 tasks remaining</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agreements;
